import React, { PureComponent } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Card } from 'semantic-ui-react';
import COLORS from 'constants/colors';
import { Warning } from 'styled-icons/material/Warning';
import {
  TEACHER_CONTROLS_TO_HIDE_TOOL_BAR,
  TILES_TO_HIDE,
  AWS_TIMER,
  RETRY_TIME,
  MATHIGON_ACTIVITY_QUERY,
} from '../constants';
import { Separator, Text, Row, Button } from '@cuemath/comps';

import { openModal, closeModal } from 'actions/modal';
import { MODAL_FORM } from 'constants/form';
import {
  dataURIToBlob,
  jsonToBlob,
  handleAWSS3Upload,
  loadScript,
  getAWSSignedKey,
} from '../utils';

const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: ${({ close }) => (close ? `0.1` : `1`)};
  pointer-events: ${({ close }) => (close ? `none` : `all`)};
`;

const CanvasContainer = styled.div`
  height: 100vh;
  max-height: 500px;
  opacity: ${({ isTransacting }) => (isTransacting ? 0.3 : 1)};
  pointer-events: ${({ isTransacting }) => (isTransacting ? 'none' : 'all')};
  position: relative;
  transition: opacity 0.3s ease-in;
  user-select: none;
  width: 100%;
  overflow: auto;
`;
const StyledRow = styled(Row)`
  height: 100%;
  position: relative;
  user-select: none;
  width: 100%;
`;

const WarningIcon = styled(Warning)`
  position: absolute;
  top: 40px;
  width: 40px;
  height: 40px;
  transform: translateY(-50%);
  margin: 0 auto;
  color: ${COLORS.COLA};
`;

const AlertCard = styled(Card)`
  position: absolute;
  height: 230px;
  min-width: 400px;
  margin: 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  border-radius: 16px;
  z-index: ${({
    theme: {
      ZINDEX: { ALERTCARD },
    },
  }) => ALERTCARD};
  opacity: 1;
  margin-bottom: 20px;
  pointer-events: all;
`;

const AlertModalRow = styled(Row)`
  padding: 20px;
  width: 400px;
  position: absolute;
`;

const ButtonNo = styled.div`
  position: relative;
  background: ${COLORS.WHITE};
  border: 1.76087px solid ${COLORS.GRAY_LIGHT};
  box-sizing: border-box;
  border-radius: 24.6522px;
  width: 135px;
  height: 32px;
  cursor: pointer;
  margin-left: 10px;
`;

const ButtonYes = styled.div`
  position: relative;
  background: ${COLORS.CERULEAN};
  border-radius: 28px;
  width: 135px;
  height: 32px;
  cursor: pointer;
`;

const AlertStyle = styled(Row)`
  width: 240px;
  margin: 0 auto;
`;

const ExitText = styled(Text)`
  padding: 10px;
  margin-top: 60px;
`;

const NoText = styled.div`
  position: relative;
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: ${({ color }) => color};
  display: flex;
  justify-content: center;
  top: 5px;
`;

const CenterAlignedDiv = styled(Text)`
  justify-content: center;
`;

let polypad;
const requestAnimationIds = {};

export const AWS_GET_SIGNEDKEY_TIMEOUT = {
  timer: AWS_TIMER, // 30mins
  id: null,
};

class CreateActivity extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showConfirmationPopup: false,
    };
  }

  static propTypes = {
    actions: PropTypes.shape({
      postSubmitData: PropTypes.func.isRequired,
      closeModal: PropTypes.func.isRequired,
    }).isRequired,
    programs: PropTypes.array.isRequired,
    isEditSheet: PropTypes.shape({}).isRequired,
  };

  componentDidMount = async () => {
    const { awsSignedKey } = this.state;

    this.downloadMathigonScript();
    this.downloadMathigonPolyfillScript();

    if (!awsSignedKey || (awsSignedKey && awsSignedKey.isStale)) {
      const awsSignedKey = await getAWSSignedKey(MATHIGON_ACTIVITY_QUERY);

      this.setState({
        awsSignedKey,
      });
    }
  };

  componentWillUnmount() {
    Object.keys(requestAnimationIds).forEach(key => {
      window.cancelAnimationFrame(requestAnimationIds[key]);
      delete requestAnimationIds[key];
    });

    clearTimeout(this.mathigonScriptTimer);
    clearTimeout(this.mathigonPolyfillScriptTimer);

    // cleanup polypad
    if (polypad) {
      polypad.off('change', this.onPolypadChange);
    }

    polypad = null;
    this.unmounted = true;
  }

  onPolypadChange = () => {
    document.getElementById('create_activity_button').style.display = 'block';
    document.getElementById('create_activity_button').style.color = '#fff';
    document.getElementById('create_activity_button').innerHTML = 'Create';
  };

  handleFileUploadToS3 = (files, imageId, resolve, reject) => {
    const { awsSignedKey } = this.state;
    files.forEach(file => {
      const { type } = file;
      const key =
        type === 'application/json'
          ? `media/wb-activity/${imageId}/activity_json`
          : `media/wb-activity/${imageId}/activity_thumbnail.png`;

      handleAWSS3Upload(file, key, awsSignedKey, resolve, reject);
    });
  };

  onCreateActivityClick = async () => {
    const arrBlobs = [];
    const { awsSignedKey } = this.state;
    const { bucketName } = awsSignedKey;

    await polypad.pngImage(200, 200).then(value => {
      const blobOBJ = dataURIToBlob(value);
      arrBlobs.push(blobOBJ);
    });

    const blobOBJJson = jsonToBlob(polypad.serialize());

    arrBlobs.push(blobOBJJson);

    const imageId = `${uuidv4()}`;

    this.props.actions.openModal(
      MODAL_FORM.ADD_NEW_ACTIVITY_MODAL,
      {
        blocking: true,
        size: 'custom',
        width: '835px',
        height: '600px',
      },
      {
        action: 'CREATE',
        programs: this.props.programs,
        getFilteredPrograms: this.props.getFilteredPrograms,
        worksheetId: imageId,
        blobs: arrBlobs,
        handleActivityDataUpload: this.handleActivityDataUpload,
        bucketName,
      },
    );
  };

  handleActivityDataUpload = async (files, imageId) => {
    return new Promise((resolve, reject) => {
      this.handleFileUploadToS3(files, imageId, resolve, reject);
    });
  };

  handleImageUploadBoardToS3 = (file, resolve, reject) => {
    console.log('TIMING: uploading started', new Date());

    const { awsSignedKey } = this.state;
    const { name } = file;

    const imageId = `${name}_${uuidv4()}`;
    const key = `media/wb-activity/${imageId}/tile_image.png`;

    handleAWSS3Upload(file, key, awsSignedKey, resolve, reject);
  };

  handleImageUploadBoard = async file => {
    return new Promise((resolve, reject) => {
      this.handleImageUploadBoardToS3(file, resolve, reject);
    });
  };

  updateToolBarUI = () => {
    const style = document.createElement('style');
    let styleHTML = '';

    TEACHER_CONTROLS_TO_HIDE_TOOL_BAR.forEach(key => {
      styleHTML += `.tool[title^="${key}"] { display: none; }`;
    });

    style.innerHTML = styleHTML;

    this.shadowRoot.appendChild(style);
  };

  updateSidebarUI = () => {
    TILES_TO_HIDE.forEach(key => {
      this.shadowRoot.querySelector(`[data-key="${key}"]`).style.display = 'none';
    });

    // to hide magic egg tile
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-geometry"]`)
      .querySelectorAll('section')[2]
      .querySelectorAll('div')[2].style.display = 'none';
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-geometry"]`)
      .querySelectorAll('section')[2]
      .querySelectorAll('div')[3].style.display = 'none';

    // to hide fractals
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-geometry"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[17].style.display = 'none';
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-geometry"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[18].style.display = 'none';

    // hide exploding dots
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-numbers"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[4].style.display = 'none';
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-numbers"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[5].style.display = 'none';

    // hide bucket of zeroes
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-numbers"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[9].style.display = 'none';
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-numbers"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[10].style.display = 'none';

    // hide powered by mathigon
    this.shadowRoot.querySelector(`[class="credit"]`).style.display = 'none';
    // hide caption in Kolam tiles
    this.shadowRoot.querySelectorAll(`[class="caption"]`)[1].style.display = 'none';
  };

  initializePolypad = async () => {
    const { userId, isDemo } = this.props;

    const canvasContainer = document.getElementById('canvasContainer');

    polypad = await window.Polypad.create(canvasContainer, {
      apiKey: 'cuemath',
      userKey: isDemo ? `demo_${userId}` : userId,
      noPanAndZoom: true, // enable pan and zoom
      sidebar: true, // enable sidebar
      bindKeyboardEvents: true, // enable keyboard shortcuts
      imageUpload: this.handleImageUploadBoard,
    });

    this.shadowRoot = canvasContainer.shadowRoot;

    if (this.shadowRoot) {
      this.updateSidebarUI();
      this.updateToolBarUI();
    }

    polypad.toggleSidebar(false);

    polypad.on('change', this.onPolypadChange);
  };

  downloadMathigonScript = async () => {
    if (!window.Polypad) {
      await loadScript(
        `https://static.mathigon.org/api/polypad-v2.1.js`,
        'mathigon-script',
      )
        .then(() => {
          console.log('polypad script loaded successfully');
        })
        .catch(() => {
          if (!this.unmounted) {
            this.mathigonScriptTimer = setTimeout(
              this.downloadMathigonScript,
              RETRY_TIME,
            );
          }
        });
    }

    if (window.Polypad && !this.unmounted) {
      await this.initializePolypad();
    }
  };

  downloadMathigonPolyfillScript = () => {
    loadScript(`https://mathigon.org/polyfill.js`, 'mathigon-polyfill-script')
      .then(async () => {
        if (!this.unmounted) {
          console.log('polyfill script loaded successfully');
        }
      })
      .catch(() => {
        if (!this.unmounted) {
          this.mathigonPolyfillScriptTimer = setTimeout(
            this.downloadMathigonPolyfillScript,
            RETRY_TIME,
          );
        }
      });
  };

  closeConfirmationModal = () => {
    this.setState({
      showConfirmationPopup: false,
    });
  };

  openConfirmationModal = () => {
    this.setState({
      showConfirmationPopup: true,
    });
  };

  onModalClose = () => {
    const { actions } = this.props;

    return (
      <AlertModalRow>
        <AlertCard>
          <AlertStyle justifyContent="center">
            <CenterAlignedDiv display="flex" align="center">
              <WarningIcon />
              <Separator height={15} transparent />
              <ExitText align="center">All unsaved changes will be lost.</ExitText>{' '}
            </CenterAlignedDiv>

            <Text align="center">Are you sure you want to close this window?</Text>
          </AlertStyle>
          <Separator height={15} transparent />
          <Row justifyContent="center">
            <ButtonYes onClick={actions.closeModal}>
              <NoText color={COLORS.WHITE}>Close</NoText>{' '}
            </ButtonYes>
            <ButtonNo onClick={this.closeConfirmationModal}>
              <NoText color={COLORS.SKY}>Cancel</NoText>
            </ButtonNo>
          </Row>
          <Separator height={20} transparent />
        </AlertCard>
        <Separator height={20} transparent />
      </AlertModalRow>
    );
  };

  render() {
    const { showConfirmationPopup } = this.state;
    return (
      <FormWrapper>
        {showConfirmationPopup && this.onModalClose()}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
          <Button
            label="Back"
            style={{ margin: '20px' }}
            onClick={this.openConfirmationModal}
          />

          <Button
            id="create_activity_button"
            label="Create"
            style={{ display: 'none', margin: '20px' }}
            onClick={this.onCreateActivityClick}
          />
        </div>
        <StyledRow>
          <CanvasContainer id="canvasContainer" />
        </StyledRow>
      </FormWrapper>
    );
  }
}

export default connect(null, dispatch => ({
  actions: bindActionCreators(
    {
      closeModal,
      openModal,
    },
    dispatch,
  ),
}))(CreateActivity);
